import React, { useContext, useRef, useState } from 'react';
import useUnitAudioPlayerCreator from 'hooks/useUnitAudioPlayerCreator';
import sampleWaveformData from 'resources/sample.min.json';
import SampleWaveformPlayer from 'utils/audio/SampleWaveformPlayer';
import { PlaybackProvider } from '../PlaybackContext';
import { PlayerContextType, PlayerProviderProps } from './types';

const PlayerContext = React.createContext<PlayerContextType | undefined>(
  undefined,
);

/*
 * This provider is a convenience for when components need to render the
 * player ui and its context separately.
 *
 * The player UI contains an audio element that's used to create the player. The
 * PlayerProvider allows that audio element to register itself by passing up
 * the audio element, which is used in this provider to create the audio player.
 *
 * Without this context, all components that render the player and its context
 * separately would have to attach a function to the audio element which saves
 * the instance and then passes it to useUnitAudioPlayerCreator to create the
 * player itself.
 *
 * Given the number of widgets in this app, there are a lot of instances of
 * the player being rendered in different ways, so this convenience can simplify
 * things a bit more than if there were just one instance of the player in the app
 */
export const PlayerProvider: React.FC<PlayerProviderProps> = ({
  animatePreview,
  children,
  extendedPlayback,
  ...props
}) => {
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(
    null,
  );
  const audioPlayer = useUnitAudioPlayerCreator(audioElement, {
    extendedPlayback,
  });
  const waveformPlayer = useRef(
    animatePreview ? new SampleWaveformPlayer(sampleWaveformData) : undefined,
  );

  return (
    <PlayerContext.Provider value={{ setAudioElement }}>
      <PlaybackProvider
        {...props}
        animatePreview={animatePreview}
        player={audioPlayer}
        previewPlayer={waveformPlayer.current}
      >
        {children}
      </PlaybackProvider>
    </PlayerContext.Provider>
  );
};

export function usePlayer() {
  const context = useContext(PlayerContext);

  if (context === undefined) {
    throw new Error('usePlayer must be used within PlayerProvider');
  }

  return context;
}
